/**
 * Dimension / aspect-ratio helpers for Gear Exchange imports.
 *
 * Gear Exchange returns Width/Height = 0 for VIDEO assets (images carry real
 * values), so video ratio detection falls back entirely to the filename. That
 * fallback originally required a full pixel resolution (`\d{3,4}x\d{3,4}`), which
 * does not match Ford's common ratio-token naming — "Capri_video_9x16.mp4" found
 * nothing, returned {0,0}, and `bestFitRatio` treated unknown dimensions as 1x1.
 * Every 9:16 video therefore imported into the 1x1 feed zone while 9:16 images
 * imported correctly.
 *
 * `bestFitRatio` routes to three zones (1x1 / 4x5 / 9x16) since 4:5 became a
 * separate upload slot on Meta and LinkedIn. Formats that have no 4:5 slot are
 * unaffected: the modal falls the 4x5 zone back to 1x1 for them.
 *
 * Lives in its own module so it can be unit-tested without importing the modal's
 * heavy browser-only dependencies (same pattern as assetsContentSignature.ts).
 */

/** Extract resolution from a filename, e.g. "..._1920x1080_..." → {w:1920,h:1080}. */
export function extractResolutionFromFilename(filename: string): { w: number; h: number } | null {
  // Full pixel resolution first — most specific.
  const px = filename.match(/(\d{3,4})x(\d{3,4})/i);
  if (px) return { w: parseInt(px[1]), h: parseInt(px[2]) };

  // Bare ratio token ("_9x16.mp4", "-4x5-"). Delimited on both sides so we never
  // match digits embedded in a longer token. Only the w/h RATIO matters
  // downstream, so {w:9,h:16} is equivalent to {w:1080,h:1920}.
  const ratio = filename.match(/(?:^|[_\-\s.])(\d{1,2})x(\d{1,2})(?:[_\-\s.]|$)/i);
  if (ratio) return { w: parseInt(ratio[1]), h: parseInt(ratio[2]) };

  return null;
}

/** Effective dimensions — API values when present, else the filename fallback. */
export function getEffectiveDimensions(asset: {
  width: number;
  height: number;
  filename: string;
}): { w: number; h: number } {
  if (asset.width > 0 && asset.height > 0) return { w: asset.width, h: asset.height };
  return extractResolutionFromFilename(asset.filename) ?? { w: 0, h: 0 };
}

/** Human-readable aspect ratio label, or null when dimensions are unknown. */
export function calcAspectRatio(w: number, h: number): string | null {
  if (!w || !h) return null;
  const r = w / h;
  if (Math.abs(r - 1) < 0.05) return '1x1';
  if (Math.abs(r - 4 / 5) < 0.05) return '4x5';
  if (Math.abs(r - 9 / 16) < 0.05) return '9x16';
  if (Math.abs(r - 16 / 9) < 0.05) return '16x9';
  if (Math.abs(r - 4 / 3) < 0.05) return '4x3';
  if (Math.abs(r - 3 / 4) < 0.05) return '3x4';
  return `${w}x${h}`;
}

/**
 * Which import zone an asset belongs to. Unknown dimensions default to 1x1.
 *
 * 4:5 is its own zone now that Meta and LinkedIn take it as a separate upload.
 * Formats without a 4:5 slot still accept these assets — the modal falls the
 * 4x5 zone back to 1x1 there, which is where they landed before this split.
 *
 * Bands are set on the midpoints between the three target ratios (1.0, 0.8,
 * 0.5625) so every asset goes to its nearest one: >= 0.9 is square, >= 0.7 is
 * feed portrait, anything taller is a story.
 */
export function bestFitRatio(w: number, h: number): '1x1' | '4x5' | '9x16' {
  if (!w || !h) return '1x1';
  const r = w / h;
  if (r >= 0.9) return '1x1';
  if (r >= 0.7) return '4x5';
  return '9x16';
}
